import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';

export const ProfileGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  // If AuthContext is still booting up the session, wait silently
  if (loading) {
    return null;
  }

  // If user is not logged in, securely redirect to login page
  if (!user) {
    console.log('[ProfileGuard] No user session found, redirecting to login page');
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }

  // If a user is registered, check if their profile setup is completed
  if (user && profile) {
    const hasDisplayName = !!profile.displayName?.trim();
    const hasPhone = !!profile.phone?.trim();
    const hasClass = !!profile.class?.trim();
    const hasSchool = !!profile.school?.trim();
    const hasBatch = !!profile.batch?.trim();

    // Strict BD mobile format validator
    const bdPhoneRegex = /^(?:\+88)?01[3-9]\d{8}$/;
    const isPhoneValid = bdPhoneRegex.test(profile.phone || '');

    const isComplete = hasDisplayName && hasPhone && isPhoneValid && hasClass && hasSchool && hasBatch;

    // Reject navigation to protected states and forward securely to profile setup
    if (!isComplete && location.pathname !== '/profile') {
      console.warn(`[ProfileGuard] User profile incomplete, redirecting to /profile. Fields checklist:`, {
        hasDisplayName,
        hasPhone,
        isPhoneValid,
        hasClass,
        hasSchool,
        hasBatch
      });
      return <Navigate to="/profile" state={{ forceComplete: true }} replace />;
    }
  }

  return <>{children}</>;
};
